import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DuplicatePreference, UploadReason, UploadStatus } from '../../types/generic';

export interface UploadState {
	deviceOwner: string | 'DEVICE_OWNER_UNKNOWN';
	deviceOwnerType: string;
	uploadReason: UploadReason;
	useCase: string;
	useCaseConfirmed: boolean;
	currentFileName: string;
	currentFileFormat: string;
	fileOperation: string;
	blockCount: number;
	uploadedBlocks: string[];
	status?: UploadStatus;
	statusDesc: string;
	blockSize: number;
	uploadId: string;
	uploadType?: string;
	warningMessage?: string;
	duplicatePreference: DuplicatePreference;
	enrichments: string[];
}

const initialState: UploadState = {
	deviceOwner: '',
	deviceOwnerType: 'Unspecified',
	uploadReason: { reason: '', onBehalfOf: false, onBehalfOfName: undefined, onBehalfOfEmail: undefined },
	useCase: '',
	useCaseConfirmed: false,
	currentFileName: '',
	currentFileFormat: '',
	fileOperation: '',
	blockCount: 0,
	uploadedBlocks: [],
	statusDesc: '',
	//blockSize: 1024 * 512, // smaller number for testing
	blockSize: 1024 * 1024 * 8, // 8mb default
	uploadId: '',
	duplicatePreference: 'DISCARD',
	enrichments: [],
};

const uploadSlice = createSlice({
	name: 'upload',
	initialState,
	reducers: {
		cancelFileUpload: (state) => {
			return {
				...initialState,
				deviceOwner: state.deviceOwner,
				deviceOwnerType: state.deviceOwnerType,
				uploadReason: state.uploadReason,
			};
		},
		resetFileUpload: () => {
			return {
				...initialState,
			};
		},
		setDeviceOwner: (state, action: PayloadAction<string>) => {
			return {
				...state,
				deviceOwner: action.payload,
			};
		},
		setDeviceOwnerType: (state, action: PayloadAction<string>) => {
			return {
				...state,
				deviceOwnerType: action.payload,
			};
		},
		initFileUpload: (
			state,
			action: PayloadAction<{
				fileName: string;
				fileFormat: string;
				fileContainer: string;
				blockCount: number;
				uploadId: string;
				uploadType?: string;
				warningMessage?: string;
			}>
		) => {
			return {
				...state,
				currentFileName: action.payload.fileName,
				currentFileFormat: action.payload.fileFormat,
				fileOperation: action.payload.fileContainer,
				blockCount: action.payload.blockCount,
				uploadedBlocks: [],
				status: UploadStatus.UPLOADING,
				statusDesc: '',
				uploadId: action.payload.uploadId,
				uploadType: action.payload.uploadType,
				warningMessage: action.payload.warningMessage,
			};
		},
		skipFileUpload: (
			state,
			action: PayloadAction<{ fileName: string; fileFormat: string; fileContainer: string; processingId: string; warningMessage: string }>
		) => {
			return {
				...state,
				currentFileName: action.payload.fileName,
				currentFileFormat: action.payload.fileFormat,
				fileOperation: action.payload.fileContainer,
				blockCount: 0,
				uploadedBlocks: [],
				status: UploadStatus.COMPLETE,
				statusDesc: '',
				uploadId: action.payload.processingId,
				warningMessage: action.payload.warningMessage,
			};
		},
		updateStatus: (
			state,
			action: PayloadAction<{ newStatus: UploadStatus; desc?: string; uploadType?: string; warningMessage?: string }>
		) => {
			return {
				...state,
				status: action.payload.newStatus,
				statusDesc: action.payload.desc || state.statusDesc,
				uploadType: action.payload.uploadType || state.uploadType,
				warningMessage: action.payload.warningMessage || state.warningMessage,
			};
		},
		addBlock: (state, action: PayloadAction<string>) => {
			if (state.status === UploadStatus.UPLOADING) {
				return {
					...state,
					uploadedBlocks: [...state.uploadedBlocks, action.payload],
				};
			}
		},
		setUploadReason: (state, action: PayloadAction<UploadReason>) => {
			return {
				...state,
				uploadReason: action.payload,
			};
		},
		setDuplicatePreference: (state, action: PayloadAction<DuplicatePreference>) => {
			return {
				...state,
				duplicatePreference: action.payload,
			};
		},
		setUseCase: (state, action: PayloadAction<{ useCase: string; confirmed: boolean }>) => {
			return {
				...state,
				useCase: action.payload.useCase,
				useCaseConfirmed: action.payload.confirmed,
			};
		},
		setFileFormat: (state, action: PayloadAction<string>) => {
			return {
				...state,
				currentFileFormat: action.payload,
			};
		},
		setEnrichments: (state, action: PayloadAction<string[]>) => {
			return {
				...state,
				enrichments: action.payload,
			};
		},
	},
});

export const getUploadComplete = createSelector([(state) => state.upload.status], (uploadStatus) => {
	return uploadStatus === UploadStatus.COMPLETE;
});

export const getUploadStarted = createSelector([(state) => state.upload.status], (uploadStatus) => {
	return uploadStatus !== undefined;
});

export const isSuspectKnown = createSelector([(state) => state.upload.deviceOwner], (suspectName) => {
	return suspectName !== 'DEVICE_OWNER_UNKNOWN';
});

export const getUploadData = createSelector([(state) => state, (state) => state.upload], (state, uploadData: UploadState) => {
	return {
		fileName: uploadData.currentFileName,
		fileOperation: uploadData.fileOperation,
		deviceOwner: uploadData.deviceOwner,
		deviceOwnerType: uploadData.deviceOwnerType,
		suspectKnown: isSuspectKnown(state),
		uploadComplete: getUploadComplete(state),
		fileId: uploadData.uploadId,
		uploadReason: uploadData.uploadReason,
		duplicatePreference: uploadData.duplicatePreference,
		useCase: uploadData.useCase,
		enrichments: uploadData.enrichments,
	};
});

export const {
	cancelFileUpload,
	resetFileUpload,
	setDeviceOwner,
	setDeviceOwnerType,
	initFileUpload,
	skipFileUpload,
	updateStatus,
	addBlock,
	setUploadReason,
	setDuplicatePreference,
	setUseCase,
	setFileFormat,
	setEnrichments,
} = uploadSlice.actions;
export default uploadSlice.reducer;
